/**
 * connectivityEvents.ts
 * -----------------------------------------------------------------
 * src/shared/events/connectivityEvents.ts
 *
 * ver1 - 29 October 2026 (Offline capture sync fix - Layer 4):
 *   A tiny shared noticeboard for connection events.
 *
 * Why it exists:
 *   useOnlineStatus (shared) knows when the LukaDuka server becomes
 *   reachable again after an outage. Modules (the offline capture queue
 *   today; uploads, Capsules etc. later) want to react to that moment.
 *   Shared code must not reach into a module, so the hook EMITS here and
 *   modules SUBSCRIBE here. Dependencies point one way: modules -> shared.
 *
 * Usage:
 *   import { onServerReachableAgain } from '@/shared/events/connectivityEvents';
 *   const unsubscribe = onServerReachableAgain(() => { ... });
 * -----------------------------------------------------------------
 */

type ConnectivityListener = () => void;

const serverReachableListeners = new Set<ConnectivityListener>();

/**
 * Subscribe to "the server is reachable again" (it was unreachable, now it
 * answers). Returns an unsubscribe function. Listeners may be called more
 * than once per recovery, so they must be safe to repeat.
 */
export function onServerReachableAgain(callback: ConnectivityListener): () => void {
  serverReachableListeners.add(callback);
  return () => {
    serverReachableListeners.delete(callback);
  };
}

/** Called by useOnlineStatus only. */
export function emitServerReachableAgain(): void {
  for (const listener of serverReachableListeners) {
    try {
      listener();
    } catch (err) {
      console.error('[connectivityEvents] listener error:', err);
    }
  }
}
